import {configureStore, getDefaultMiddleware} from '@reduxjs/toolkit'

// Redux Store Configuration
// Optimized for performance with large datasets (exercices, exams, etc.)
import AuthReducer from './reducers/auth/slice'
import LicenseReducer from './reducers/licenses/slice'
import UsersReducer from './reducers/users/slice'
import EstablishmentsReducer from './reducers/establishements/slice'
import ClassesReducer from './reducers/classes/slice'
import SubjectsReducer from './reducers/subjects/slice'
import ExercicesReducer from './reducers/exercices/slice'
import VideoGalleryReducer from './reducers/videoGallery/slice'
import ExamsReducer from './reducers/exams/slice'
import TypeExamsReducer from './reducers/typeExams/slice'
import RemarquesReducer from './reducers/remarques/slice'
import TypeRemarquesReducer from './reducers/typeRemarques/slice'
import PaymentCategoriesReducer from './reducers/paymentCategories/slice'
import PaymentsReducer from './reducers/payments'
import ExpendituresReducer from './reducers/expenditures/slice'
import PeriodsReducer from './reducers/periods/slice'
import NotificationsReducer from './reducers/notifications/slice'
import GlobalReducer from './reducers/global/globalSlice'
import ChatbotReducer from './reducers/chatbot/slice'
import InventoryReducer from './reducers/inventory/slice'
import InventoryTypeReducer from './reducers/inventoryType/slice'
import EmailsReducer from './reducers/emails/slice'
import PayslipReducer from './reducers/payroll/slice'
import PayrollCategoryReducer from './reducers/payrollCategories/slice'
import GradesReducer from './reducers/grades/slice'
import ZadCategoriesReducer from './reducers/zadCategories/slice'
import ZadProductTypesReducer from './reducers/zadProductTypes/slice'
import ZadImportReducer from './reducers/zadImport/slice'
import ZadProductsReducer from './reducers/zadProducts/slice'
import ZadStockReducer from './reducers/zadStock/slice'
import ZadSuppliersReducer from './reducers/zadSuppliers/slice'
import ZadPurchasesReducer from './reducers/zadPurchases/slice'
import ZadSupplierPaymentsReducer from './reducers/zadSupplierPayments/slice'
import ZadCustomersReducer from './reducers/zadCustomers/slice'
import ZadSalesReducer from './reducers/zadSales/slice'
import ZadCustomerPaymentsReducer from './reducers/zadCustomerPayments/slice'
import ZadCustomerReturnsReducer from './reducers/zadCustomerReturns/slice'
import ZadCustomerRefundsReducer from './reducers/zadCustomerRefunds/slice'
import ZadReportsReducer from './reducers/zadReports/slice'
import ZadSalesHistoryReducer from './reducers/zadSalesHistory/slice'
import ZadDeliveryNotesReducer from './reducers/zadDeliveryNotes/slice'
import ZadIssuerProfileReducer from './reducers/zadIssuerProfile/slice'
import ZadSalesValidationPermissionReducer from './reducers/zadSalesValidationPermission/slice'
import ZadUnpaidFollowupsReducer from './reducers/zadUnpaidFollowups/slice'
import ZadCatalogueReducer from './reducers/zadCatalogue/slice'
import ZadPromotionsReducer from './reducers/zadPromotions'
import ZadPromoCodesReducer from './reducers/zadPromoCodes'
import ZadReplenishmentReducer from './reducers/zadReplenishment/slice'
import ZadProcurementReducer from './reducers/zadProcurement/slice'
import ZadLabelBatchesReducer from './reducers/zadLabelBatches/slice'

export const store = configureStore({
  reducer: {
    auth: AuthReducer,
    licenses: LicenseReducer,
    users: UsersReducer,
    establishments: EstablishmentsReducer,
    classes: ClassesReducer,
    subjects: SubjectsReducer,
    exercices: ExercicesReducer,
    videoGallery: VideoGalleryReducer,
    exams: ExamsReducer,
    typeExams: TypeExamsReducer,
    remarques: RemarquesReducer,
    typeRemarques: TypeRemarquesReducer,
    paymentCategories: PaymentCategoriesReducer,
    payments: PaymentsReducer,
    expenditures: ExpendituresReducer,
    periods: PeriodsReducer,
    notifications: NotificationsReducer,
    global: GlobalReducer,
    chatbot: ChatbotReducer,
    inventory: InventoryReducer,
    inventoryType: InventoryTypeReducer,
    emails: EmailsReducer,
    grades: GradesReducer,
    payroll: PayslipReducer,
    payrollCategories: PayrollCategoryReducer,
    zadCategories: ZadCategoriesReducer,
    zadProductTypes: ZadProductTypesReducer,
    zadImport: ZadImportReducer,
    zadProducts: ZadProductsReducer,
    zadStock: ZadStockReducer,
    zadSuppliers: ZadSuppliersReducer,
    zadPurchases: ZadPurchasesReducer,
    zadSupplierPayments: ZadSupplierPaymentsReducer,
    zadCustomers: ZadCustomersReducer,
    zadSales: ZadSalesReducer,
    zadCustomerPayments: ZadCustomerPaymentsReducer,
    zadCustomerReturns: ZadCustomerReturnsReducer,
    zadCustomerRefunds: ZadCustomerRefundsReducer,
    zadReports: ZadReportsReducer,
    zadSalesHistory: ZadSalesHistoryReducer,
    zadDeliveryNotes: ZadDeliveryNotesReducer,
    zadIssuerProfile: ZadIssuerProfileReducer,
    zadSalesValidationPermission: ZadSalesValidationPermissionReducer,
    zadUnpaidFollowups: ZadUnpaidFollowupsReducer,
    zadCatalogue: ZadCatalogueReducer,
    zadPromotions: ZadPromotionsReducer,
    zadPromoCodes: ZadPromoCodesReducer,
    zadReplenishment: ZadReplenishmentReducer,
    zadProcurement: ZadProcurementReducer,
    zadLabelBatches: ZadLabelBatchesReducer,
  },
  middleware: getDefaultMiddleware({
    immutableCheck: {
      ignoredPaths: ['inventory.inventoryItems', 'users.users', 'exercices.exercices', 'exams.exams', 'videoGallery.videos'],
      warnAfter: 64,
    },
    serializableCheck: {
      ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      ignoredPaths: ['inventory.inventoryItems', 'users.users', 'exercices.exercices', 'exams.exams', 'videoGallery.videos'],
      warnAfter: 64,
    },
  }),
  devTools: process.env.NODE_ENV !== 'production',
})

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
